// GLSL for the war network (docs/ART-DIRECTION.md §1 war network, §2.1 owner by colour + shape).
// InstancedMesh defines USE_INSTANCING / USE_INSTANCING_COLOR, so `instanceMatrix` and `instanceColor` come
// from the ShaderMaterial prefix. All outputs are additive (colour × alpha added to the emissive target).
// City art is authored in CSS px and placed by CITY_ART_GLSL (cityArt.ts); rings are antialiased hairlines computed
// per fragment in the part's px space, so a stroke keeps its pixel width at every zoom.

import { CITY_ART_GLSL } from './cityArt'

/**
 * Fragment chunk: coverage of a ring of half-width `halfWidth` around radius `centre` (both in the part's px), with
 * `aa` = fwidth of the radius (px per device pixel). A stroke thinner than a pixel fades instead of breaking up.
 */
const RING_COVERAGE_GLSL = /* glsl */ `
float ringCoverage(float r, float centre, float halfWidth, float aa) {
  float width = max(halfWidth, 0.5 * aa);
  return min(1.0, halfWidth / width) * clamp((width - abs(r - centre)) / aa + 0.5, 0.0, 1.0);
}
`

/**
 * City diamond / capital octahedron: optional spin about local +Y (the surface normal), fresnel edges. The instance
 * matrix scales the unit octahedron to the node's half extents in px (non-uniformly for city diamonds), so normals go
 * through the inverse-transpose of that basis (dividing by the squared axis scales, as three's defaultnormal_vertex
 * does). uLiftPx puts the node's centre above the surface.
 */
export const NODE_VERTEX = /* glsl */ `
${CITY_ART_GLSL}
uniform float uSpin;
uniform float uLiftPx;

varying vec3 vColor;
varying vec3 vNormal;
varying vec3 vView;

void main() {
  float c = cos(uSpin);
  float s = sin(uSpin);
  mat3 spin = mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c);
  mat3 instanceBasis = mat3(instanceMatrix);
  vec3 axisScaleSq = vec3(dot(instanceBasis[0], instanceBasis[0]), dot(instanceBasis[1], instanceBasis[1]), dot(instanceBasis[2], instanceBasis[2]));
  vec4 mvPosition = modelViewMatrix * cityArtWorldPosition(spin * position, uLiftPx);
  vNormal = normalize(normalMatrix * (instanceBasis * ((spin * normal) / axisScaleSq)));
  vView = normalize(-mvPosition.xyz);
  vColor = instanceColor;
  gl_Position = projectionMatrix * mvPosition;
}
`

export const NODE_FRAGMENT = /* glsl */ `
uniform float uFill;
uniform float uRim;
uniform float uRimPower;

varying vec3 vColor;
varying vec3 vNormal;
varying vec3 vView;

void main() {
  float facing = abs(dot(normalize(vNormal), normalize(vView)));
  float glow = uFill + uRim * pow(1.0 - facing, uRimPower);
  gl_FragColor = vec4(vColor * glow, 1.0);
}
`

/**
 * Owner rim under every city (the capital's is its halo): a hairline ring on a unit quad. aRimDashes encodes the
 * owner by shape: 0 = solid, otherwise that many dashes around the ring.
 */
export const RIM_VERTEX = /* glsl */ `
${CITY_ART_GLSL}
attribute float aRimDashes;
attribute float aRadiusPx;
attribute float aStrokePx;
uniform float uMarginPx;

varying vec3 vColor;
varying vec2 vLocalPx;
varying float vDashes;
varying float vRadiusPx;
varying float vStrokePx;

void main() {
  vLocalPx = position.xy * (aRadiusPx + uMarginPx);
  vDashes = aRimDashes;
  vRadiusPx = aRadiusPx;
  vStrokePx = aStrokePx;
  vColor = instanceColor;
  gl_Position = projectionMatrix * modelViewMatrix * cityArtWorldPosition(vec3(vLocalPx, 0.0), 0.0);
}
`

export const RIM_FRAGMENT = /* glsl */ `
uniform float uIntensity;
uniform float uDashDuty;

varying vec3 vColor;
varying vec2 vLocalPx;
varying float vDashes;
varying float vRadiusPx;
varying float vStrokePx;

const float TAU = 6.28318530718;
const float MIN_RADIUS_PX = 1e-3;

${RING_COVERAGE_GLSL}

/** Coverage of the dash [0, duty) of a cycle at phase f, antialiased over fw cycles per device pixel. */
float dashCoverage(float f, float duty, float fw) {
  float inside = min(f, duty - f);
  float outside = min(f - duty, 1.0 - f);
  return clamp((f < duty ? inside : -outside) / fw + 0.5, 0.0, 1.0);
}

void main() {
  float r = length(vLocalPx);
  float aa = fwidth(r);
  float halfStroke = 0.5 * vStrokePx;
  float cover = ringCoverage(r, vRadiusPx - halfStroke, halfStroke, aa);
  if (vDashes > 0.5) {
    float cycles = (atan(vLocalPx.y, vLocalPx.x) / TAU + 0.5) * vDashes;
    // Arc length per device pixel in dash cycles (no fwidth of the angle: it jumps at the atan seam).
    float fw = vDashes * aa / (TAU * max(r, MIN_RADIUS_PX));
    cover *= dashCoverage(fract(cycles), uDashDuty, fw);
  }
  if (cover <= 0.0) discard;
  gl_FragColor = vec4(vColor * (uIntensity * cover), 1.0);
}
`

/** Capital sigil (ART-DIRECTION §2.3): a flat star in the owner's colour, floating uLiftPx above the surface. */
export const SIGIL_VERTEX = /* glsl */ `
${CITY_ART_GLSL}
uniform float uLiftPx;

varying vec3 vColor;

void main() {
  vColor = instanceColor;
  gl_Position = projectionMatrix * modelViewMatrix * cityArtWorldPosition(position, uLiftPx);
}
`

export const SIGIL_FRAGMENT = /* glsl */ `
uniform float uIntensity;

varying vec3 vColor;

void main() {
  gl_FragColor = vec4(vColor * uIntensity, 1.0);
}
`

/** Great-circle arcs: per-vertex owner colour, sea edges dashed with the dash pattern flowing a → b. */
export const EDGE_VERTEX = /* glsl */ `
attribute vec3 aColor;
attribute float aDistance;
attribute float aDashed;

varying vec3 vColor;
varying float vDistance;
varying float vDashed;

void main() {
  vColor = aColor;
  vDistance = aDistance;
  vDashed = aDashed;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

export const EDGE_FRAGMENT = /* glsl */ `
uniform float uIntensity;
uniform float uDashPeriod;
uniform float uDashPhase;
uniform float uDashDuty;

varying vec3 vColor;
varying float vDistance;
varying float vDashed;

void main() {
  float cycle = fract(vDistance / uDashPeriod - uDashPhase);
  if (vDashed > 0.5 && cycle > uDashDuty) discard;
  gl_FragColor = vec4(vColor * uIntensity, 1.0);
}
`

/**
 * Owned-territory glow (ART-DIRECTION §1): a flat unit disc per city, scaled to its glow radius in px by the instance
 * matrix, with a soft radial falloff in the owner's colour. Neutral cities are painted black, which adds nothing.
 */
export const TERRITORY_VERTEX = /* glsl */ `
${CITY_ART_GLSL}
varying vec3 vColor;
varying vec2 vLocal;

void main() {
  vLocal = position.xy;
  vColor = instanceColor;
  gl_Position = projectionMatrix * modelViewMatrix * cityArtWorldPosition(position, 0.0);
}
`

export const TERRITORY_FRAGMENT = /* glsl */ `
uniform float uIntensity;

varying vec3 vColor;
varying vec2 vLocal;

void main() {
  float falloff = 1.0 - clamp(length(vLocal), 0.0, 1.0);
  gl_FragColor = vec4(vColor * uIntensity * falloff * falloff, 1.0);
}
`

/**
 * City status arcs on a unit quad: the HP arc (outer, owner's colour, only while damaged) and inside it the capture arc
 * (capturer's colour, only while a capture runs). Both are hairlines filling clockwise from north over a dim track. A
 * city at full HP and not being captured draws nothing.
 */
export const STATUS_VERTEX = /* glsl */ `
${CITY_ART_GLSL}
attribute float aHp;
attribute float aCapture;
attribute vec3 aCaptureColor;
attribute float aRadiusPx;
uniform float uMarginPx;

varying vec3 vColor;
varying vec3 vCaptureColor;
varying vec2 vLocalPx;
varying float vRadiusPx;
varying float vHp;
varying float vCapture;

void main() {
  vLocalPx = position.xy * (aRadiusPx + uMarginPx);
  vRadiusPx = aRadiusPx;
  vColor = instanceColor;
  vCaptureColor = aCaptureColor;
  vHp = aHp;
  vCapture = aCapture;
  gl_Position = projectionMatrix * modelViewMatrix * cityArtWorldPosition(vec3(vLocalPx, 0.0), 0.0);
}
`

export const STATUS_FRAGMENT = /* glsl */ `
uniform float uIntensity;
uniform float uTrack;
uniform float uHpStroke;
uniform float uCaptureStroke;
uniform float uGap;

varying vec3 vColor;
varying vec3 vCaptureColor;
varying vec2 vLocalPx;
varying float vRadiusPx;
varying float vHp;
varying float vCapture;

const float TAU = 6.28318530718;
const float MIN_RADIUS_PX = 1e-3;

${RING_COVERAGE_GLSL}

void main() {
  bool damaged = vHp < 1.0;
  bool capturing = vCapture > 0.0;
  if (!damaged && !capturing) discard;
  float r = length(vLocalPx);
  float aa = fwidth(r);
  // Clockwise from north (+Y) toward east (+X), as seen from above.
  float turn = fract(atan(vLocalPx.x, vLocalPx.y) / TAU + 1.0);
  float turnAa = aa / (TAU * max(r, MIN_RADIUS_PX));
  float hpCover = damaged ? ringCoverage(r, vRadiusPx - 0.5 * uHpStroke, 0.5 * uHpStroke, aa) : 0.0;
  float captureCentre = vRadiusPx - uHpStroke - uGap - 0.5 * uCaptureStroke;
  float captureCover = capturing ? ringCoverage(r, captureCentre, 0.5 * uCaptureStroke, aa) : 0.0;
  if (hpCover + captureCover <= 0.0) discard;
  float hpLit = mix(uTrack, uIntensity, clamp((vHp - turn) / turnAa + 0.5, 0.0, 1.0));
  float captureLit = mix(uTrack, uIntensity, clamp((vCapture - turn) / turnAa + 0.5, 0.0, 1.0));
  gl_FragColor = vec4(vColor * (hpCover * hpLit) + vCaptureColor * (captureCover * captureLit), 1.0);
}
`
